import { Trans, useTranslation } from "react-i18next";
import { useState } from "react";
import { StepMedia } from "./StepMedia.js";
import type { ManualBodyStep, StepPayload, ManualMediaRef } from "@arc/contracts";
import { cn } from "@/lib/utils";

/**
 * The interactive part of a typed step (ADR-002 Phase 6b) — Question / QuestionWithScore (answerable, with correct/failed
 * feedback), Checklist (tickable), ShowScore (the passing threshold). Regular steps render nothing here (just text+media).
 * Payload strings are plain text (rendered as text nodes); the completed/failed feedback is server-sanitized HTML.
 */
export function StepInteractive({ step }: { step: ManualBodyStep }) {
  const p = step.payload;
  if (!p) return null;
  switch (p.kind) {
    case "question":
    case "question_with_score":
      return <QuestionPlayer payload={p} completedText={step.completedText} failedText={step.failedText} completedMedia={step.completedMedia} failedMedia={step.failedMedia} stepName={step.name} />;
    case "checklist":
      return <ChecklistPlayer payload={p} />;
    case "show_score":
      return <ShowScorePlayer scoreLevel={p.scoreLevel} />;
  }
}

type QuestionPayload = Extract<StepPayload, { kind: "question" | "question_with_score" }>;

function QuestionPlayer({
  payload,
  completedText,
  failedText,
  completedMedia,
  failedMedia,
  stepName,
}: {
  payload: QuestionPayload;
  completedText: string | null;
  failedText: string | null;
  /** The state's own image / video (ADR-027 §11 B), shown with its text. */
  completedMedia?: ManualMediaRef | null;
  failedMedia?: ManualMediaRef | null;
  stepName?: string;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<number | null>(null);
  const answers =
    payload.kind === "question"
      ? payload.answers.map((text, i) => ({ text, correct: i === payload.correctIndex }))
      : payload.answers.map((a) => ({ text: a.text, correct: a.correct }));
  const answered = selected != null;
  const isCorrect = answered && (answers[selected]?.correct ?? false);
  return (
    <section aria-label={t("viewer.question")} className="border-border flex flex-col gap-3 rounded-md border p-4">
      {payload.question ? <p className="font-medium">{payload.question}</p> : null}
      <ul role="radiogroup" className="flex list-none flex-col gap-2 p-0">
        {answers.map((a, i) => (
          <li key={i}>
            <button
              type="button"
              role="radio"
              aria-checked={selected === i}
              onClick={() => setSelected(i)}
              className={cn(
                "w-full cursor-pointer rounded-md border px-3 py-2 text-left text-sm transition-colors",
                answered && a.correct
                  ? "border-green-600 bg-green-600/10"
                  : answered && selected === i && !a.correct
                    ? "border-destructive bg-destructive/10"
                    : selected === i
                      ? "border-primary"
                      : "border-border hover:bg-muted",
              )}
            >
              {a.text}
            </button>
          </li>
        ))}
      </ul>
      {answered ? (
        <div className={cn("rounded-md p-3 text-sm", isCorrect ? "bg-green-600/10" : "bg-destructive/10")}>
          <p className="font-medium">{isCorrect ? t("settings.correctAnswer") : t("viewer.notQuite")}</p>
          {/* Server-sanitized feedback HTML (ADR-013). */}
          {isCorrect && completedText ? <div dangerouslySetInnerHTML={{ __html: completedText }} /> : null}
          {isCorrect && completedMedia ? <StepMedia media={completedMedia} name={stepName ?? ""} /> : null}
          {!isCorrect && failedText ? <div dangerouslySetInnerHTML={{ __html: failedText }} /> : null}
          {!isCorrect && failedMedia ? <StepMedia media={failedMedia} name={stepName ?? ""} /> : null}
        </div>
      ) : null}
    </section>
  );
}

type ChecklistPayload = Extract<StepPayload, { kind: "checklist" }>;

function ChecklistPlayer({ payload }: { payload: ChecklistPayload }) {
  const { t } = useTranslation();
  const [checked, setChecked] = useState<Set<number>>(new Set());
  return (
    <section aria-label={t("viewer.checklist")} className="border-border flex flex-col gap-2 rounded-md border p-4">
      {payload.text ? <p className="font-medium">{payload.text}</p> : null}
      <ul className="flex list-none flex-col gap-2 p-0">
        {payload.items.map((item, i) => (
          <li key={i}>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={checked.has(i)}
                onChange={() =>
                  setChecked((prev) => {
                    const next = new Set(prev);
                    if (next.has(i)) next.delete(i);
                    else next.add(i);
                    return next;
                  })
                }
                className="size-4"
              />
              <span>{item}</span>
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ShowScorePlayer({ scoreLevel }: { scoreLevel: number }) {
  const { t } = useTranslation();
  return (
    <section aria-label={t("viewer.score")} className="border-border rounded-md border p-4 text-sm">
      <p className="font-medium">{t("viewer.score")}</p>
      <p className="text-muted-foreground">
        <Trans
          i18nKey="viewer.passingScore"
          components={{ score: <span className="tabular-nums">{scoreLevel}%</span> }}
        />
      </p>
    </section>
  );
}
